import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import { AtsSkeleton, Button, BUTTON_TYPES, Card, STATUS_TYPES, Tooltip } from '_atoms';
import { Typography, TYPOGRAPHY_WEIGHT } from '_atoms/Typography';
import { Status } from '_atoms/Status';
import { getJobStatusType } from '_molecules/JobHeader/utils';
import { getHRLJobsDashboardAPI, getRecruiterLeadJobsDashboardAPI } from 'actions';
import { JOB_STATUSES } from 'constants/job';
import type { JobStatus } from 'constants/job';
import { ReactComponent as JobImage } from 'dist/emptyStates/dashboardOpening.svg';
import './style.scss';
import { useTranslation } from 'react-i18next';
import type { DashboardJobRow } from 'actions/dashboardActions';

interface NewJobsProps {
  trigger?: unknown;
  isHrl?: boolean;
}

export const NewJobs: React.FC<NewJobsProps> = React.memo(({ trigger, isHrl }) => {
  const [jobs, setJobs] = useState<DashboardJobRow[]>([]);
  const [allShown, setAllShown] = useState(false);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const onShowMoreClick = () => setAllShown(s => !s);

  useEffect(() => {
    setLoading(true);
    (isHrl ? getHRLJobsDashboardAPI() : getRecruiterLeadJobsDashboardAPI())
      .then(resp => setJobs(resp as unknown as DashboardJobRow[]))
      .finally(() => setLoading(false));
  }, [trigger]);

  if (loading) {
    return (
      <Card className="new-jobs">
        <div className="new-jobs__header">
          {t('translation:new_jobs.title')}
          <span className="number">{jobs.length}</span>
        </div>
        <div className="new-jobs__content-loading">
          <AtsSkeleton height={140} />
        </div>
      </Card>
    );
  }

  if (!jobs.length) {
    return (
      <Card className="new-jobs">
        <div className="new-jobs__header">
          {t('translation:new_jobs.title')}
          <span className="number">0</span>
        </div>
        <div className="new-jobs__content-empty">
          <JobImage className="empty-image" />
          <Typography.Title3 weight={TYPOGRAPHY_WEIGHT.BOLD}>
            {t('translation:new_jobs.text_no_new_jobs')}
          </Typography.Title3>
        </div>
      </Card>
    );
  }

  return (
    <Card className={cn('new-jobs', { allShown })}>
      <div className="new-jobs__header">
        {t('translation:new_jobs.title')}
        <span className="number">({jobs.length})</span>
      </div>
      <div className="new-jobs__content">
        {jobs.map(job => (
          <div key={job.uuid} className="new-job">
            <div className="new-job__info">
              <Tooltip label={job.name}>
                <Link to={`/jobs/${job.uuid}/show/description`} target="_blank">
                  {job.name}
                </Link>
              </Tooltip>
              <Typography.Caption>{job.service}</Typography.Caption>
            </div>
            <div className="new-job__status">
              <Status type={getJobStatusType(job.status)}>{job.status}</Status>
            </div>
            <div className="new-job__opening-status">
              {!!job.opening_status && (
                <>
                  <Typography.Caption>{t('translation:new_jobs.label_opening_status')}</Typography.Caption>
                  <Status type={STATUS_TYPES.BLUE}>{job.opening_status}</Status>
                  {job.openings_count > 1 && (
                    <Status type={STATUS_TYPES.GRAY}>
                      + {job.openings_count - 1} {t('translation:new_jobs.text_more')}
                    </Status>
                  )}
                </>
              )}
            </div>
            <div className="new-job__action">
              {([JOB_STATUSES.APPROVED, JOB_STATUSES.OPEN] as JobStatus[]).includes(job.status as JobStatus) && (
                <Link to={`/jobs/${job.uuid}/show/description?team=true`} target="_blank">
                  <Button type={BUTTON_TYPES.PRIMARY}>{t('translation:new_jobs.button_assign_recruiters')}</Button>
                </Link>
              )}
            </div>
          </div>
        ))}
        <Button className="show-more" type={BUTTON_TYPES.TEXT} onClick={onShowMoreClick}>
          {allShown ? t('translation:new_jobs.button_show_less') : t('translation:new_jobs.button_show_more')}
        </Button>
      </div>
    </Card>
  );
});
